/**
 * Deterministic SVG identicon derived from an Ethereum address.
 * No external dependencies — pure address → SVG mapping.
 */

interface AccountIdenticonProps {
  address: string;
  size?: number;
  className?: string;
}

/**
 * Parse first 8 hex chars after 0x into a symmetric 4×4 pixel grid.
 * Mirrors left→right so only 2 columns need generating.
 */
function buildGrid(address: string): boolean[][] {
  const hex = address.replace(/^0x/i, "").slice(0, 8);
  const bytes = [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
    parseInt(hex.slice(6, 8), 16),
  ];

  // 4 rows × 2 source columns (mirrored to 4)
  const grid: boolean[][] = [];
  for (let row = 0; row < 4; row++) {
    const byte = bytes[row] ?? 0;
    const col0 = (byte & 0x80) !== 0;
    const col1 = (byte & 0x40) !== 0;
    grid.push([col0, col1, col1, col0]); // mirror
  }
  return grid;
}

function deriveHue(address: string): number {
  const hex = address.replace(/^0x/i, "").slice(4, 8);
  return parseInt(hex, 16) % 360;
}

export function AccountIdenticon({
  address,
  size = 24,
  className,
}: AccountIdenticonProps) {
  const grid = buildGrid(address);
  const hue = deriveHue(address);
  const color = `hsl(${String(hue)}, 65%, 55%)`;
  const bgColor = `hsl(${String(hue)}, 40%, 20%)`;

  const cellSize = size / 6; // 4 cells + 1 cell padding each side
  const offset = cellSize; // 1-cell padding

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${String(size)} ${String(size)}`}
      className={className}
      aria-hidden="true"
    >
      <circle cx={size / 2} cy={size / 2} r={size / 2} fill={bgColor} />
      {grid.map((row, ri) =>
        row.map((on, ci) =>
          on ? (
            <rect
              key={`${String(ri)}-${String(ci)}`}
              x={offset + ci * cellSize}
              y={offset + ri * cellSize}
              width={cellSize}
              height={cellSize}
              rx={cellSize * 0.15}
              fill={color}
            />
          ) : null,
        ),
      )}
    </svg>
  );
}
